import { useState, useEffect } from "react";
import boardService from "../services/boardService";

const useBoardSelection = () => {
  // State for boards
  const [boards, setBoards] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [boardsError, setBoardsError] = useState(null);

  // State for new board dialog
  const [newBoardDialogOpen, setNewBoardDialogOpen] = useState(false);
  const [newBoardData, setNewBoardData] = useState({
    name: "",
    description: "",
    template: "offerStatus",
  });
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [createBoardError, setCreateBoardError] = useState(null);

  // State for menu anchors
  const [boardMenuAnchor, setBoardMenuAnchor] = useState(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  // State for column selection
  const [selectedBoard, setSelectedBoard] = useState(null);

  // State for showing "Take me to board" option
  const [showGoToBoard, setShowGoToBoard] = useState(false);
  const [lastAddedBoard, setLastAddedBoard] = useState(null);

  const boardMenuOpen = Boolean(boardMenuAnchor);
  const columnMenuOpen = Boolean(columnMenuAnchor);

  // Fetch boards when menu is opened
  useEffect(() => {
    if (boardMenuOpen && boards.length === 0) {
      fetchBoards();
    }
  }, [boardMenuOpen, boards.length]);

  // Fetch boards from service
  const fetchBoards = async () => {
    setLoadingBoards(true);
    setBoardsError(null);

    try {
      const fetchedBoards = await boardService.getBoards();
      setBoards(fetchedBoards);
    } catch (error) {
      console.error("Error fetching boards:", error);
      setBoardsError("Failed to load boards. Please try again.");
    } finally {
      setLoadingBoards(false);
    }
  };

  const handleBoardMenuClick = (event) => {
    setBoardMenuAnchor(event.currentTarget);
    // Reset any previous selection
    setSelectedBoard(null);
    setColumnMenuAnchor(null);
  };

  const handleBoardMenuClose = () => {
    setBoardMenuAnchor(null);
  };

  const handleBoardSelect = (board) => {
    setSelectedBoard(board);
    setColumnMenuAnchor(boardMenuAnchor);
    setBoardMenuAnchor(null);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
    setSelectedBoard(null);
  };

  const handleCloseGoToBoard = () => {
    setShowGoToBoard(false);
    setLastAddedBoard(null);
  };

  const handleAddToBoard = async (athlete, boardId, columnId, onSuccess) => {
    try {
      // Validate required parameters
      if (!boardId) {
        console.error("Board ID is missing");
        return;
      }
      if (!columnId) {
        console.error("Column ID is missing");
        return;
      }

      // Ensure we have the athlete ID
      const prospectData = {
        id: athlete.id || athlete._id,
        firstName: athlete.firstName,
        lastName: athlete.lastName,
        position: athlete.position,
        highSchool: athlete.highSchool || athlete.school,
        city: athlete.city || athlete.address?.city,
        state: athlete.state || athlete.address?.state,
        graduationYear: athlete.graduationYear,
        image: athlete.profilePicture || athlete.image,
        color: athlete.color,
        notes: Array.isArray(athlete.notes)
          ? athlete.notes
              .map((note) => (typeof note === "string" ? note : note.text))
              .join("; ")
          : athlete.notes || "",
      };

      await boardService.addProspectToBoard(boardId, columnId, prospectData);

      // Get board and column names for the confirmation message
      const board = boards.find((b) => b.id === boardId || b._id === boardId);
      const column = board?.columns?.find(
        (c) => c.id === columnId || c._id === columnId
      );
      const boardName = board?.name || "Board";
      const columnName = column?.title || "Column";

      if (onSuccess) {
        onSuccess(boardId, columnId, boardName, columnName);
      }

      // Set state to show "Take me to board" option
      setLastAddedBoard(board);
      setShowGoToBoard(true);

      handleColumnMenuClose();
    } catch (error) {
      console.error("Error adding prospect to board:", error);
      throw error;
    }
  };

  const handleNewBoardDialogOpen = () => {
    setNewBoardDialogOpen(true);
    handleBoardMenuClose();
  };

  const handleNewBoardDialogClose = () => {
    setNewBoardDialogOpen(false);
    setNewBoardData({ name: "", description: "", template: "offerStatus" });
    setCreateBoardError(null);
  };

  const handleNewBoardInputChange = (e) => {
    const { name, value } = e.target;
    setNewBoardData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateBoard = async (athlete, onSuccess) => {
    // Validate board name
    if (!newBoardData.name.trim()) {
      setCreateBoardError("Board name is required");
      return;
    }

    setCreatingBoard(true);
    setCreateBoardError(null);

    try {
      const newBoard = await boardService.createBoard(newBoardData);
      setBoards((prev) => [...prev, newBoard]);

      // Automatically select the first column of the new board
      if (newBoard.columns && newBoard.columns.length > 0) {
        await handleAddToBoard(
          athlete,
          newBoard.id,
          newBoard.columns[0].id,
          onSuccess
        );
      }

      handleNewBoardDialogClose();
    } catch (error) {
      console.error("Error creating board:", error);
      setCreateBoardError("Failed to create board. Please try again.");
    } finally {
      setCreatingBoard(false);
    }
  };

  return {
    // State
    boards,
    loadingBoards,
    boardsError,
    newBoardDialogOpen,
    newBoardData,
    creatingBoard,
    createBoardError,
    boardMenuAnchor,
    columnMenuAnchor,
    selectedBoard,
    showGoToBoard,
    lastAddedBoard,
    boardMenuOpen,
    columnMenuOpen,

    // Actions
    handleBoardMenuClick,
    handleBoardMenuClose,
    handleBoardSelect,
    handleColumnMenuClose,
    handleCloseGoToBoard,
    handleAddToBoard,
    handleNewBoardDialogOpen,
    handleNewBoardDialogClose,
    handleNewBoardInputChange,
    handleCreateBoard,
    fetchBoards,
  };
};

export default useBoardSelection;
